import { tKey, type I18nKey } from "@/core/i18n";
import { Button } from "@/modules/shadcn/ui/button";
import { Label } from "@/modules/shadcn/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shadcn/ui/select";
import { Spinner } from "@/modules/shadcn/ui/spinner";
import { Camera } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/** One selectable camera, whatever it was read from. */
export interface CameraOption {
  mxid: string;
  label: string;
}

/**
 * Where the list came from. The copy has to say, because "no cameras" means
 * something different for a typed address than for a remote device, and a list
 * read from a device's last heartbeat is not evidence the camera is reachable
 * now.
 */
export type CameraSource =
  /** Nothing to detect against yet. */
  | { kind: "none"; reason: "no-url" | "no-device" }
  /** Live, from a typed local address. */
  | { kind: "local" }
  /** A remote device — `live` false means the relay was unusable and this is
   *  the device's last heartbeat. */
  | { kind: "device"; live: boolean };

interface CameraSelectFieldProps {
  cameras: CameraOption[] | null;
  source: CameraSource;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  value: string | null;
  onChange: (mxid: string | null) => void;
}

const placeholderFor = (
  source: CameraSource,
  noCameras: boolean,
): I18nKey<"project"> => {
  if (source.kind === "none") {
    return source.reason === "no-url"
      ? tKey("project:camera.placeholder.noUrl")
      : tKey("project:camera.placeholder.noDevice");
  }

  if (!noCameras) {
    return tKey("project:camera.placeholder.select");
  }

  if (source.kind === "local") {
    return tKey("project:camera.placeholder.noneDetected");
  }

  // A live empty list is authoritative; a heartbeat one only says the device
  // never told us about a camera, which is a different thing to go check.
  return source.live
    ? tKey("project:camera.placeholder.noneOnDevice")
    : tKey("project:camera.placeholder.noneReported");
};

export const CameraSelectField = ({
  cameras,
  source,
  isLoading,
  isError,
  onRetry,
  value,
  onChange,
}: CameraSelectFieldProps) => {
  const { t } = useTranslation(["project", "common"]);
  // The stored camera may be absent from the detected list (unplugged, other
  // API). Keep it selectable-as-is in the trigger instead of clearing it.
  const valueNotDetected =
    value != null && cameras != null && !cameras.some((c) => c.mxid === value);

  const items = useMemo(() => {
    const record: Record<string, string> = {};
    for (const camera of cameras ?? []) {
      record[camera.mxid] = camera.label;
    }
    if (value && !(value in record)) {
      record[value] =
        cameras != null ? t("camera.notDetected", { mxid: value }) : value;
    }
    return record;
  }, [cameras, value, t]);

  const noCamerasDetected = cameras != null && cameras.length === 0;
  const disabled =
    isLoading || isError || source.kind === "none" || (noCamerasDetected && !value);

  const hint =
    source.kind === "none"
      ? source.reason === "no-url"
        ? t("camera.hintNoUrl")
        : t("camera.hintNoDevice")
      : source.kind === "device" && !source.live
        ? t("camera.hintHeartbeat")
        : t("camera.hint");

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="projectCamera" className="font-semibold">
        {t("camera.label")}
      </Label>
      <div className="flex flex-row items-center gap-2">
        <Select
          value={value}
          onValueChange={(val) => onChange(val)}
          items={items}
          disabled={disabled}
        >
          <SelectTrigger id="projectCamera" className="w-full flex-1">
            {isLoading ? (
              <span className="flex items-center gap-2 text-muted-foreground">
                <Spinner className="size-4" />
                {t("camera.detecting")}
              </span>
            ) : (
              <SelectValue placeholder={t(placeholderFor(source, noCamerasDetected))}>
                {(mxid: string) => (
                  <span className="flex items-center gap-2">
                    <Camera className="size-4" />
                    {items[mxid] ?? mxid}
                  </span>
                )}
              </SelectValue>
            )}
          </SelectTrigger>
          <SelectContent>
            {cameras?.map((camera) => (
              <SelectItem key={camera.mxid} value={camera.mxid}>
                <span className="flex items-center gap-2">
                  <Camera className="size-4" />
                  {camera.label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(isError || noCamerasDetected) && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            {t("common:actions.retry")}
          </Button>
        )}
      </div>
      {isError && (
        <p className="text-xs text-destructive">
          {source.kind === "device"
            ? t("camera.unreachableDevice")
            : t("camera.unreachableUrl")}
        </p>
      )}
      {valueNotDetected && !isLoading && !isError && (
        <p className="text-xs text-amber-600">
          {source.kind === "device"
            ? t("camera.missingOnDevice")
            : t("camera.missingAtUrl")}
        </p>
      )}
      <p className="text-xs text-black/50">{hint}</p>
    </div>
  );
};
